import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import { type SupportRequestAttachment, type SupportRequestCommentEntity } from "@fitness-app/data-models";
import { getErrorMessage } from "@fitness-app/utils";

import { supabase } from "~/store/initializeStore";
import { callSupportApi } from "./useSupportApi";
import { SUPPORT_COMMENTS_QUERY_KEY, type SupportPerson } from "../supportConstants";

export type SupportCommentWithAuthor = SupportRequestCommentEntity & {
  author: SupportPerson | null;
};

export const useSupportComments = (supportRequestId: string | null) => {
  const { data, isLoading } = useQuery({
    queryKey: [SUPPORT_COMMENTS_QUERY_KEY, supportRequestId],
    enabled: Boolean(supportRequestId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_request_comment")
        .select("*, author:authorUserId(id, email, firstName, lastName)")
        .eq("supportRequestId", supportRequestId!)
        .order("createdAt", { ascending: true });

      if (error) {
        throw error;
      }

      return (data || []) as unknown as SupportCommentWithAuthor[];
    },
  });

  return { comments: data || [], isLoading };
};

export const useAddSupportComment = (supportRequestId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    // Goes through the API (not a direct Supabase insert) so the server can validate
    // mentions and fan out the mention notifications (TRE-1550).
    mutationFn: async ({
      body,
      mentions,
      attachments,
    }: {
      body: string;
      mentions: string[];
      attachments?: SupportRequestAttachment[];
    }) => {
      await callSupportApi<{ comment: SupportRequestCommentEntity }>("add-comment", {
        id: supportRequestId!,
        body,
        mentions,
        attachments,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUPPORT_COMMENTS_QUERY_KEY, supportRequestId] });
    },
    onError: (error) => {
      message.error(getErrorMessage(error));
    },
  });
};

export const useUpdateSupportComment = (supportRequestId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    // Direct Supabase write — RLS ("support comment own update") limits it to the
    // author, so no dedicated endpoint is needed. Mentions are left untouched: an
    // edit does not re-run the mention fan-out, so a newly typed @name won't notify.
    mutationFn: async ({ commentId, body }: { commentId: string; body: string }) => {
      const { data, error } = await supabase
        .from("support_request_comment")
        .update({ body, updatedAt: new Date().toISOString() })
        .eq("id", commentId)
        .select("id");

      if (error) {
        throw error;
      }

      // RLS silently filters rows we cannot touch — 0 updated rows means "not yours".
      if (!data?.length) {
        throw new Error("Możesz edytować tylko własne komentarze");
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUPPORT_COMMENTS_QUERY_KEY, supportRequestId] });
    },
    onError: (error) => {
      message.error(getErrorMessage(error));
    },
  });
};

export const useDeleteSupportComment = (supportRequestId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ commentId }: { commentId: string }) => {
      const { data, error } = await supabase.from("support_request_comment").delete().eq("id", commentId).select("id");

      if (error) {
        throw error;
      }

      if (!data?.length) {
        throw new Error("Możesz usuwać tylko własne komentarze");
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUPPORT_COMMENTS_QUERY_KEY, supportRequestId] });
    },
    onError: (error) => {
      message.error(getErrorMessage(error));
    },
  });
};
